import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import ScheduleEditor from "@/components/team/ScheduleEditor";
import { TEAM } from "@/constants/testIds";

const EMPTY_FORM = {
  name: "", email: "", password: "", role: "professional", phone: "",
  specialty: "", photo_url: null, schedule: [], custom_role_id: null,
};

const TEAM_PLACEHOLDER = "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function TeamMemberModal({ open, onOpenChange, member, onSubmit, roles = [] }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(member);

  useEffect(() => {
    if (member) {
      setForm({
        name: member.name || "",
        email: member.email || "",
        password: "",
        role: member.role || "professional",
        phone: member.phone || "",
        specialty: member.specialty || "",
        photo_url: member.photo_url || null,
        schedule: member.schedule || [],
        custom_role_id: member.custom_role_id || null,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [member, open]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Fix AW (2026-10-27): el selector mezcla roles fijos y roles personalizados ("custom:<id>")
  const roleValue = form.role === "custom" ? `custom:${form.custom_role_id || ""}` : form.role;
  const changeRole = (v) => setForm((f) => (
    v.startsWith("custom:")
      ? { ...f, role: "custom", custom_role_id: v.slice(7) }
      : { ...f, role: v, custom_role_id: null }
  ));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSubmit(form, member);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar miembro del equipo" : "Nuevo miembro del equipo"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ImageUpload
            currentUrl={form.photo_url}
            onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
            placeholderUrl={TEAM_PLACEHOLDER}
            testId={TEAM.modalPhotoUpload}
          />

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre completo</Label>
            <Input value={form.name} onChange={set("name")} data-testid={TEAM.modalNameInput} placeholder="Ej: Dra. Isabella Fernandez" />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Email</Label>
            <Input type="email" value={form.email} onChange={set("email")} disabled={isEdit} data-testid={TEAM.modalEmailInput} placeholder="correo@stranijustbecause.com" />
          </div>

          {!isEdit && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Contraseña temporal</Label>
              <Input type="password" value={form.password} onChange={set("password")} data-testid={TEAM.modalPasswordInput} placeholder="Mínimo 6 caracteres" />
            </div>
          )}

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Teléfono</Label>
            <Input value={form.phone} onChange={set("phone")} data-testid={TEAM.modalPhoneInput} placeholder="Opcional" />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Rol en el sistema</Label>
            <Select value={roleValue} onValueChange={changeRole}>
              <SelectTrigger data-testid={TEAM.modalRoleSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="professional">Profesional</SelectItem>
                <SelectItem value="reception">Recepcion / Asistente</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.id} value={`custom:${r.id}`}>{r.name} (rol personalizado)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.role === "professional" && (
            <>
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Especialidad</Label>
                <Input value={form.specialty} onChange={set("specialty")} data-testid={TEAM.modalSpecialtyInput} placeholder="Ej: Dermatología Estética" />
              </div>
              <ScheduleEditor value={form.schedule} onChange={(s) => setForm((f) => ({ ...f, schedule: s }))} />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={TEAM.modalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={TEAM.modalSaveButton}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
